import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { storeKeys } from '@/services/queryKeys';
import {
  storeService,
  type StoreSettings,
  type ThemeTokens,
} from '@/services/store.service';

export function useStoreSettings() {
  return useQuery({
    queryKey: storeKeys.settings(),
    queryFn: () => storeService.getSettings(),
    retry: 0,
  });
}

/** Inventory tracking settings for a concrete branch. Backend uses these
 * branch flags as the source of truth; warehouse lot flags are legacy-only. */
export function useStoreInventorySettings(storeId?: string) {
  return useQuery({
    queryKey: storeKeys.storeSettings(storeId ?? ''),
    queryFn: () => storeService.getStoreSettings(storeId!),
    enabled: Boolean(storeId),
    staleTime: 60_000,
    retry: 0,
  });
}

export function useUpdateStoreSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<StoreSettings>) => storeService.updateSettings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.settings() }),
  });
}

export function useTheme() {
  return useQuery({
    queryKey: storeKeys.theme(),
    queryFn: () => storeService.getTheme(),
    staleTime: 60_000,
    retry: 0,
  });
}

export function useUpdateTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ThemeTokens>) => storeService.updateTheme(body),
    onSuccess: (data) => {
      qc.setQueryData(storeKeys.theme(), data);
      qc.invalidateQueries({ queryKey: storeKeys.theme() });
    },
  });
}

/** List stores (chi nhánh) để populate Select gán nhân viên (EG-85 §2). */
export function useStores(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: storeKeys.stores(),
    queryFn: () => storeService.listStores(),
    staleTime: 60_000,
    retry: 0,
    enabled: options.enabled ?? true,
  });
}

/** Single store detail (EG-354). */
export function useStore(id: string) {
  return useQuery({
    queryKey: storeKeys.store(id),
    queryFn: () => storeService.getStore(id),
    enabled: !!id,
    retry: 0,
  });
}

/** Create store mutation (EG-354). */
export function useCreateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      code?: string;
      address: string;
      phone?: string;
      taxCode?: string;
      managerId?: string;
      parentStoreId?: string | null;
      openingHours?: string;
      active?: boolean;
    }) => storeService.createStore(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.stores() }),
  });
}

/** Update store mutation (EG-354). */
export function useUpdateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      code?: string;
      address?: string;
      phone?: string;
      taxCode?: string;
      managerId?: string;
      parentStoreId?: string | null;
      openingHours?: string;
      active?: boolean;
    }) => storeService.updateStore(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      qc.invalidateQueries({ queryKey: storeKeys.store(vars.id) });
    },
  });
}

/** Deactivate store mutation (EG-354). */
export function useDeactivateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storeService.deactivateStore(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      qc.invalidateQueries({ queryKey: storeKeys.store(id) });
    },
  });
}

/** Activate store mutation (EG-354). */
export function useActivateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storeService.activateStore(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      qc.invalidateQueries({ queryKey: storeKeys.store(id) });
    },
  });
}

/** Delete store mutation (EG-1796). */
export function useDeleteStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storeService.deleteStore(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      qc.invalidateQueries({ queryKey: storeKeys.store(id) });
      qc.invalidateQueries({ queryKey: ['plan'] });
    },
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => storeService.uploadLogo(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.settings() }),
  });
}
